"use client";

import { useUser } from "@clerk/nextjs";
import { type FormEvent, memo, PropsWithChildren, useState } from "react";
import {
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useInView } from "react-intersection-observer";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Filter } from "../filter/filter";
import { List } from "./list";
import { DownloadList } from "./download-list";
import { ListInfo } from "./list-info";
import { SearchCity } from "../search/city";
import { SearchState } from "../search/state";
import { CreateAlert } from "./create-alert";
import { RequiredBuyPremiumButton } from "@/app/_components/required/buy-premium-button";

import { useFilter } from "../use-filter";

import { modalityCriteria, typeCriteria } from "@/constants";
import { cn } from "@/lib/utils";
import { getUserPlanByMetadata } from "@/helper/user/client";

import type { House, HouseCityParam, ListProps, PriceRange } from "@/types";

const priceMinMaxProperties = (
  data: House[]
): {
  priceRange: PriceRange;
} => {
  let minPriceFound = data.reduce(
    (min, property) => Math.min(min, property.preco_atual || 0),
    Infinity
  );
  let maxPriceFound = data.reduce(
    (max, property) => Math.max(max, property.preco_atual || 0),
    -Infinity
  );
  if (minPriceFound === Infinity) minPriceFound = 0;
  if (maxPriceFound === -Infinity) maxPriceFound = 0;

  return {
    priceRange: {
      min: minPriceFound,
      max: maxPriceFound,
    },
  };
};

interface Props {
  hideSEOTag?: boolean;
  target?: HTMLAnchorElement["target"];
  fetchProperties: (
    params: Partial<HouseCityParam>
  ) => Promise<{ properties: House[]; totalCount: number }>;
}

const SEOTag = memo(
  ({
    children,
    hideSEOTag,
  }: PropsWithChildren<Omit<Props, "fetchProperties">>) => {
    if (hideSEOTag) {
      return (
        <p className="text-sm text-center text-muted-foreground">{children}</p>
      );
    }

    return (
      <h1 className="text-sm text-center text-muted-foreground">{children}</h1>
    );
  }
);

SEOTag.displayName = "SEOTag";

export const ListContent = ({
  properties: data = [],
  uf,
  info,
  hideSEOTag,
  target,
  fetchProperties,
}: ListProps & Props) => {
  const { user } = useUser();
  const { hasPaidPlan } = getUserPlanByMetadata(user?.publicMetadata);
  const [page, setPage] = useState(1);
  const [properties, setProperties] = useState(data);
  const [totalCount, setTotalCount] = useState(info.totalCount);
  const [loading, setLoading] = useState(false);

  const hasMoreData = properties.length < totalCount;

  const [
    {
      cidade,
      ordenar,
      tipo,
      modalidade,
      precoMin,
      precoMax,
      pagamento,
      banco,
      pesquisa,
      tamanhoArea,
      tamanhoPropriedade,
    },
    setSearchParam,
  ] = useFilter();

  const { priceRange } = priceMinMaxProperties(properties);

  const fetchData = async (
    initialPage: HouseCityParam["pagina"],
    hideLoading?: boolean
  ) => {
    setPage(initialPage);

    if (!hideLoading) setLoading(true);

    const { properties: newData, totalCount } = await fetchProperties({
      cidade,
      ordenar,
      tipo,
      modalidade,
      precoMin,
      precoMax,
      pagamento,
      banco,
      tamanhoArea,
      tamanhoPropriedade,
      pesquisa: pesquisa?.trim(),
      pagina: initialPage,
    });

    if (!hideLoading) setLoading(false);
    setProperties(initialPage === 1 ? newData : [...properties, ...newData]);
    setTotalCount(totalCount);
  };

  const cleanData = async () => {
    setLoading(true);

    setSearchParam({
      cidade: null,
      modalidade: null,
      pagamento: null,
      banco: null,
      precoMax: null,
      precoMin: null,
      ordenar: null,
      tipo: null,
      pesquisa: null,
      tamanhoArea: null,
      tamanhoPropriedade: null,
    });

    const { properties: newData, totalCount } = await fetchProperties({
      pagina: 1,
    });

    setLoading(false);
    setProperties(newData);
    setTotalCount(totalCount);
    setPage(1);
  };

  const search = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    fetchData(1);
  };

  const cleanSearch = async () => {
    setLoading(true);

    setSearchParam({
      pesquisa: null,
    });

    const { properties: newData, totalCount } = await fetchProperties({
      pagina: 1,
    });

    setLoading(false);
    setProperties(newData);
    setTotalCount(totalCount);
    setPage(1);
  };

  const { ref, inView } = useInView({
    threshold: 0.5,
    onChange: async (inView) => {
      if (inView && hasMoreData) {
        await fetchData(page + 1, true);
      }
    },
  });

  return (
    <>
      <div className="flex flex-col gap-4 items-center justify-center">
        <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
          <div className="w-28">
            <SearchState filter={uf} />
          </div>
          <SearchCity
            filter={uf}
            action={{ filter: fetchData, clean: cleanData }}
          />
        </div>
        <div className="flex flex-row gap-1">
          <span className="text-sm text-center text-muted-foreground">
            {totalCount}
          </span>
          <SEOTag hideSEOTag={hideSEOTag}>
            Leilão de Imóveis
            {cidade.length > 0 ? ` em ${cidade.join(", ")}` : null}
            {cidade.length > 0 ? ` / ` : ` em `}
            {uf.toUpperCase()}
            {(tipo && tipo !== "todos") || modalidade.length > 0 ? (
              <>
                {" ("}
                {tipo && tipo !== "todos" ? typeCriteria[tipo] : null}
                {tipo && tipo !== "todos" && modalidade.length > 0
                  ? ", "
                  : null}
                {modalidade.length > 0
                  ? modalidade.map((item, index) =>
                      index >= 1 && index < modalidade.length + 1
                        ? `, ${modalityCriteria[item]}`
                        : `${modalityCriteria[item]}`
                    )
                  : null}
                {")"}
              </>
            ) : null}
          </SEOTag>
        </div>
      </div>
      <div className="w-full">
        <div className="flex flex-col sm:flex-row gap-7 justify-between items-center mb-14 w-full sm:w-auto">
          <Filter
            priceRange={priceRange}
            action={{ filter: fetchData, clean: cleanData }}
          />
          <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
            <DownloadList uf={uf}>
              <Button>Baixar planilha</Button>
            </DownloadList>
            <CreateAlert uf={uf}>
              <Button>Criar alerta</Button>
            </CreateAlert>
            <div className="hidden sm:flex flex-row gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <Cog6ToothIcon className="size-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem asChild>
                    <CreateAlert uf={uf}>
                      <Button
                        variant="ghost"
                        className="text-sm font-normal block"
                      >
                        Criar alerta
                      </Button>
                    </CreateAlert>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <DownloadList uf={uf}>
                      <Button
                        variant="ghost"
                        className="text-sm font-normal block"
                      >
                        Baixar planilha
                      </Button>
                    </DownloadList>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        <form
          role="search"
          className="w-full sm:w-[420px] mx-auto"
          onSubmit={search}
        >
          <div className="relative">
            <div className="absolute top-2.5 left-2">
              <MagnifyingGlassIcon
                className="mr-2 size-4 text-muted-foreground opacity-50"
                aria-hidden="true"
              />
            </div>
            <RequiredBuyPremiumButton>
              <Input
                type="search"
                placeholder="Buscar endereço, bairro..."
                className="w-full sm:min-w-72 pl-7 pr-10"
                value={pesquisa || ""}
                onChange={(e) => {
                  if (hasPaidPlan) {
                    setSearchParam({ pesquisa: e.target.value });
                  }
                }}
              />
            </RequiredBuyPremiumButton>
            <div
              className={cn(
                "absolute top-0.5 right-1",
                pesquisa ? "block" : "hidden"
              )}
            >
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="h-8 w-8"
                onClick={cleanSearch}
              >
                <XMarkIcon className="size-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </form>
        <List
          innerRef={ref}
          inView={inView}
          loading={loading}
          hasMoreData={hasMoreData}
          properties={properties}
          cleanSearch={cleanSearch}
          target={target}
        />
        <div className="mt-16">
          <p className="text-xs text-muted-foreground">
            * O lucro estimado é calculado com base no valor do 2º leilão. Caso
            não haja, considera-se o valor do 1º leilão.
          </p>
        </div>
        {page === 1 || properties.length === totalCount ? (
          <div className="mt-36">
            <ListInfo cities={cidade} uf={uf} />
          </div>
        ) : null}
      </div>
    </>
  );
};

ListContent.displayName = "ListContent";
